import { searchContext } from '../../../../context/Context';
import dropdownLists from '../../DropdownLists';
import { renderRoundedValues, renderJaccard } from '../renders/CommonAtributes';
import { getHeaderProps } from '../config/helpers';
import { renderMappingButton } from '../renders/buttonRenderers';
import { createSelectFilter } from '../filters/filterConfigs';
import { renderNumericColumn, renderTextColumn } from '@/utils/renderTableValue';

export const getDDAColumns = context => {
  const headerProps = getHeaderProps();

  return [
    {
      name: 'ddaRelation',
      category: 'DDA',
      sortable: true,
      header: <span title="DDA Relation">DDA Relation</span>,
      headerProps,
      minWidth: 265,
      ...createSelectFilter(dropdownLists.ddaRelation || []),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'jaccard_genes',
      category: 'DDA',
      sortable: true,
      header: (
        <span title="Jaccard index based on shared genes">
          JI<sub>g</sub>
        </span>
      ),
      headerProps,
      render: instance => {
        const result = renderTextColumn(instance?.data?.jaccard_genes);
        return result === '-' ? '-' : renderJaccard(instance, 'GENE');
      },
    },

    {
      name: 'jaccard_variants',
      category: 'DDA',
      sortable: true,
      header: (
        <span title="Jaccard index based on shared variants">
          JI<sub>v</sub>
        </span>
      ),
      headerProps,
      render: instance => {
        const result = renderTextColumn(instance?.data?.jaccard_variants);
        return result === '-' ? '-' : renderJaccard(instance, 'VARIANT');
      },
    },

    {
      name: 'sokal',
      category: 'DDA',
      sortable: true,
      header: <span title="SOKAL diseases' similarity">SOKAL</span>,
      headerProps,
      render: instance => {
        const result = renderNumericColumn(instance?.data?.sokal);
        return result === '0' ? '0.00' : renderRoundedValues(instance, 'sokal');
      },
    },

    {
      name: 'pvalue_jaccard_genes',
      category: 'DDA',
      sortable: true,
      header: (
        <span title="log10 pValue of the jaccard genes">
          -log<sub>10</sub>(pValue JI<sub>g</sub>)
        </span>
      ),
      headerProps,
      minWidth: 170,
      render: row => {
        const result = renderNumericColumn(row?.data?.pvalue_jaccard_genes);
        const displayValue = result === '-' ? '0.00' : parseFloat(result).toFixed(2);
        return <span>{displayValue}</span>;
      },
    },

    {
      name: 'pvalue_jaccard_variants',
      category: 'DDA',
      sortable: true,
      header: (
        <span title="log10 pValue of the jaccard variants">
          -log<sub>10</sub>(pValue JI<sub>v</sub>)
        </span>
      ),
      headerProps,
      minWidth: 170,
      render: row => {
        const result = renderNumericColumn(row?.data?.pvalue_jaccard_variants);
        const displayValue = result === '0' ? '0.00' : parseFloat(result).toFixed(2);
        return <span>{displayValue}</span>;
      },
    },

    {
      name: 'ngenes_diseaseID_1',
      category: 'Disease',
      sortable: true,
      header: (
        <span title="Number of genes of the index Disease">
          N genes<sub>d1</sub>
        </span>
      ),
      headerProps,
      minWidth: 95,
      render: ({ data }) => {
        const { tab } = searchContext();

        if (tab !== 'DDA') return null;

        const config = {
          displayValue: renderNumericColumn(data?.ngenes_diseaseID_1),
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.disease1_UMLSCUI,
              name: data.disease1_Name,
              id: data.disease1_UMLSCUI,
            },
          ],
          columVisibilityModelKey: 'ngenes_diseaseID',
          tabSegment: 'gda',
          name: 'ngenes_diseaseID_1',
        };

        return renderMappingButton(data, config);
      },
    },

    {
      name: 'ngenes_diseaseID_2',
      category: 'Disease',
      sortable: true,
      header: (
        <span title="Number of Genes of the associated Disease">
          N genes <sub>d2</sub>
        </span>
      ),
      headerProps,
      minWidth: 125,
      render: ({ data }) => {
        const { tab } = searchContext();

        if (tab !== 'DDA') return null;

        const config = {
          displayValue: renderNumericColumn(data?.ngenes_diseaseID_2),
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.disease2_UMLSCUI,
              name: data.disease2_Name,
              id: data.disease2_UMLSCUI,
            },
          ],
          columVisibilityModelKey: 'ngenes_diseaseID',
          tabSegment: 'gda',
          name: 'ngenes_diseaseID_2',
        };

        return renderMappingButton(data, config);
      },
    },

    {
      name: 'nvariants_diseaseID_1',
      category: 'Disease',
      sortable: true,
      header: (
        <span title="Number of variants of the index disease">
          N variants <sub>d1</sub>{' '}
        </span>
      ),
      headerProps,
      minWidth: 125,
      render: ({ data }) => {
        const { tab } = searchContext();

        if (tab !== 'DDA') return null;

        const config = {
          displayValue: renderNumericColumn(data?.nvariants_diseaseID_1),
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.disease1_UMLSCUI,
              name: data.disease1_Name,
              id: data.disease1_UMLSCUI,
            },
          ],
          columVisibilityModelKey: 'nvariants_diseaseID',
          tabSegment: 'vda',
          name: 'nvariants_diseaseID_1',
        };

        return renderMappingButton(data, config);
      },
    },

    {
      name: 'nvariants_diseaseID_2',
      category: 'Disease',
      sortable: true,
      header: (
        <span title="Number of variants of the associated disease">
          N Variants <sub>d2</sub>
        </span>
      ),
      headerProps,
      minWidth: 125,
      render: ({ data }) => {
        const { tab } = searchContext();

        if (tab !== 'DDA') return null;

        const config = {
          displayValue: renderNumericColumn(data?.nvariants_diseaseID_2),
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.disease2_UMLSCUI,
              name: data.disease2_Name,
              id: data.disease2_UMLSCUI,
            },
          ],
          columVisibilityModelKey: 'nvariants_diseaseID',
          tabSegment: 'vda',
          name: 'nvariants_diseaseID_2',
        };

        return renderMappingButton(data, config);
      },
    },

    {
      name: 'shared_genes',
      category: 'DDA',
      sortable: true,
      header: <span title="Number of shared genes between both diseases">N shared Genes</span>,
      headerProps,
      render: ({ data }) => {
        const { tab } = searchContext();

        if (tab !== 'DDA') return null;

        const config = {
          displayValue: renderNumericColumn(data?.shared_genes),
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.disease1_UMLSCUI,
              name: data.disease1_Name,
              id: data.disease1_UMLSCUI,
            },
            {
              filterParam: 'disease',
              filterValue: data.disease2_UMLSCUI,
              name: data.disease2_Name,
              id: data.disease2_UMLSCUI,
            },
          ],
          columVisibilityModelKey: 'ngenes_diseaseID',
          tabSegment: 'gda/shared',
          name: 'shared_genes',
        };

        return renderMappingButton(data, config);
      },
    },

    {
      name: 'shared_variants',
      category: 'DDA',
      sortable: true,
      header: <span title="Number of shared variants between both diseases">N shared Variants</span>,
      headerProps,
      render: ({ data }) => {
        const { tab } = searchContext();

        if (tab !== 'DDA') return null;

        const config = {
          displayValue: renderNumericColumn(data?.shared_variants),
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.disease1_UMLSCUI,
              name: data.disease1_Name,
              id: data.disease1_UMLSCUI,
            },
            {
              filterParam: 'disease',
              filterValue: data.disease2_UMLSCUI,
              name: data.disease2_Name,
              id: data.disease2_UMLSCUI,
            },
          ],
          columVisibilityModelKey: 'nvariants_diseaseID',
          tabSegment: 'vda/shared',
          name: 'shared_variants',
        };

        return renderMappingButton(data, config);
      },
    },
  ];
};
